import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../hooks/axios";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import Input from "../../components/form/input/InputField";
import AdminPageShell from "../../components/admin/AdminPageShell";
import AdminCard from "../../components/admin/AdminCard";
import AdminListToolbar from "../../components/admin/AdminListToolbar";
import {
  adminDangerLinkClass,
  adminEditLinkClass,
  adminPaginationActiveClass,
  adminPaginationInactiveClass,
  adminSpinnerClass,
  adminSpinnerSmClass,
  adminTableClass,
  adminTableHeadClass,
  adminTableTdClass,
  adminTableThSortableClass,
  adminViewLinkClass,
  adminAlertErrorClass,
} from "../../components/admin/adminStyles";
import { VehicleType } from "../../types";
import { vehicleTypeLabel } from "../../utils/vehicleTypeLabel";

interface SortConfig {
  key: keyof VehicleType | '';
  direction: 'asc' | 'desc';
}

export default function VehicleTypes() {
  const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: '', direction: 'asc' });

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchVehicleTypes();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, currentPage, pageSize, sortConfig]);

  const fetchVehicleTypes = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/vehicletypes");
      const allTypes = response.data;
      
      const filteredTypes = allTypes.filter((type: VehicleType) => {
        const searchLower = searchTerm.toLowerCase();
        return vehicleTypeLabel(type).toLowerCase().includes(searchLower);
      });

      if (sortConfig.key !== '') {
        filteredTypes.sort((a: VehicleType, b: VehicleType) => {
          const aValue = String(a[sortConfig.key as keyof VehicleType]);
          const bValue = String(b[sortConfig.key as keyof VehicleType]);
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        });
      }

      const totalItems = filteredTypes.length;
      const calculatedTotalPages = Math.ceil(totalItems / pageSize);
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      
      setTotalPages(calculatedTotalPages);
      setVehicleTypes(filteredTypes.slice(startIndex, endIndex));
    } catch (error) {
      setError("Failed to fetch vehicle types");
      console.error("Error fetching vehicle types:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this vehicle type?")) {
      try {
        await axiosInstance.delete(`/vehicletypes/${id}`);
        fetchVehicleTypes();
      } catch (error) {
        setError("Failed to delete vehicle type");
        console.error("Error deleting vehicle type:", error);
      }
    }
  };

  const handleSort = (key: keyof VehicleType) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const renderSortIcon = (key: keyof VehicleType) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  const renderPagination = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`mx-1 ${
            currentPage === i ? adminPaginationActiveClass : adminPaginationInactiveClass
          }`}
        >
          {i}
        </button>
      );
    }
    return (
      <div className="flex justify-center mt-4">
        <button
          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className={`mx-1 ${adminPaginationInactiveClass} disabled:opacity-50`}
        >
          Previous
        </button>
        {pages}
        <button
          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
          className={`mx-1 ${adminPaginationInactiveClass} disabled:opacity-50`}
        >
          Next
        </button>
      </div>
    );
  };

  return (
    <AdminPageShell>
      <PageBreadcrumb
        pageTitle="Vehicle Types"
        description="Manage fleet categories, capacity, and pricing multipliers"
      />

      <AdminListToolbar addTo="/vehicle-type/add" addLabel="Add Vehicle Type">
        <Input
          placeholder="Search vehicle types..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full"
        />
      </AdminListToolbar>

      {error && <div className={adminAlertErrorClass}>{error}</div>}

      <AdminCard padding={false}>
        <div className="overflow-x-auto">
          <table className={adminTableClass}>
            <thead className={adminTableHeadClass}>
              <tr>
                <th
                  className={adminTableThSortableClass}
                  onClick={() => handleSort('name')}
                >
                  Name {renderSortIcon('name')}
                </th>
                <th 
                  className={adminTableThSortableClass}
                  onClick={() => handleSort('seatCapacity')}
                >
                  Seat Capacity {renderSortIcon('seatCapacity')}
                </th>
                <th 
                  className={adminTableThSortableClass}
                  onClick={() => handleSort('pricePercentage')}
                >
                  Price Percentage {renderSortIcon('pricePercentage')}
                </th>
                <th 
                  className={adminTableThSortableClass}
                  onClick={() => handleSort('status')}
                >
                  Status {renderSortIcon('status')}
                </th>
                <th className={`${adminTableThSortableClass} text-end`}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-800 dark:bg-transparent">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className={adminSpinnerSmClass} />
                    </div>
                  </td>
                </tr>
              ) : vehicleTypes.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                    No vehicle types found
                  </td>
                </tr>
              ) : (
                vehicleTypes.map((vehicleType) => (
                  <tr key={vehicleType.id}>
                    <td className="px-5 py-4 text-sm font-medium text-gray-900 dark:text-white/90">
                      {vehicleTypeLabel(vehicleType)}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">
                      {vehicleType.seatCapacity || '-'}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">
                      {vehicleType.pricePercentage ? `${vehicleType.pricePercentage}%` : '-'}
                    </td>
                    <td className="px-5 py-4 text-sm text-gray-900 dark:text-white/90">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          vehicleType.status
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {vehicleType.status ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className={`${adminTableTdClass} text-end`}>
                      <div className="flex items-center justify-end gap-3">
                        <Link
                          to={`/vehicle-type/detail/${vehicleType.id}`}
                          className={adminViewLinkClass}
                        >
                          View
                        </Link>
                        <Link
                          to={`/vehicle-type/edit/${vehicleType.id}`}
                          className={adminEditLinkClass}
                        >
                          Edit
                        </Link>
                        <button
                          type="button"
                          onClick={() => handleDelete(vehicleType.id)}
                          className={adminDangerLinkClass}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && vehicleTypes.length > 0 && (
          <div className="border-t border-gray-200 px-5 py-4 dark:border-gray-800">
            <div className="flex justify-between items-center">
              <div>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="border rounded px-2 py-1"
                >
                  <option value={5}>5 per page</option>
                  <option value={10}>10 per page</option>
                  <option value={20}>20 per page</option>
                  <option value={50}>50 per page</option>
                </select>
              </div>
              {renderPagination()}
            </div>
          </div>
        )}
      </AdminCard>
    </AdminPageShell>
  );
} 